// Merkan · Hilfe-Symbol unten rechts (über Hugo) und Hilfe-Panel mit Anleitungen.
// Symbol: pausierte Tour → Fortsetzen-Zeichen mit Stand „3/7“; sonst beendete Tour → animiertes Buch; sonst keines.
// Panel: Suche, Fortsetzen-Karte, Anleitungen nach Bereich mit „Geführt zeigen“; Zeile öffnet den Artikel im Wiki.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, LifeBuoy, Play } from 'lucide-react';
import { IcSchliessen as X, IcWiki } from '../ui/icons';
import { Knopf, Marke, Suchfeld } from '../ui';
import { useSession } from '../lib/session';
import { t } from '../lib/sprache';
import { anleitungPasst, nachBereich, useAnleitungen, type Anleitung } from './anleitungen';
import { fortschrittVerwerfen, hilfeSchliessen, hilfeUmschalten, pausierteTour, symbolArt, tourFortsetzen, tourStarten, useTour } from './tour';
import SupportDialog, { supportOeffnen, type SupportVorlage } from '../seiten/benachrichtigungen/SupportDialog';
import './hilfe.css';

const bewegungAus = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Buch mit einer Seite, die beim Erscheinen, bei Hover und alle 20 s einmal umblättert */
function BuchSymbol({ blaettern }: { blaettern: number }) {
  return (
    <svg className="hi-buch" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path className="hi-buch-deckel" d="M12 6.5C10.4 5.3 8 4.75 4.75 4.75c-.55 0-1 .45-1 1v11.5c0 .55.45 1 1 1 3.1 0 5.4.6 7.25 1.75 1.85-1.15 4.15-1.75 7.25-1.75.55 0 1-.45 1-1V5.75c0-.55-.45-1-1-1C16 4.75 13.6 5.3 12 6.5Z" />
      <path className="hi-buch-ruecken" d="M12 6.5V20" />
      <g key={blaettern} className={blaettern ? 'hi-buch-seite hi-blaettert' : 'hi-buch-seite'}>
        <path d="M12 6.5c1.3-.95 3.2-1.45 5.75-1.5v11.3c-2.4.05-4.3.5-5.75 1.4Z" className="hi-buch-blatt" />
      </g>
    </svg>
  );
}

export default function HilfeSymbol({ ueberHugo, nebenHugo, verdeckt }: { ueberHugo: boolean; nebenHugo?: boolean; verdeckt?: boolean }) {
  const { mandant } = useSession();
  const { fortschritt, aktiv, panelOffen } = useTour();
  const { liste } = useAnleitungen(mandant?.mandant_id);
  const art = symbolArt(fortschritt);
  const pause = pausierteTour(fortschritt);
  const pauseAnleitung = pause && liste ? liste.find((a) => a.id === pause[0]) ?? null : null;
  const knopfRef = useRef<HTMLButtonElement>(null);
  const [support, setSupport] = useState<SupportVorlage | null>(null);
  useEffect(() => {
    const h = (e: Event) => setSupport((e as CustomEvent<SupportVorlage>).detail ?? { quelle: 'hilfe' });
    window.addEventListener('merkan:support', h);
    return () => window.removeEventListener('merkan:support', h);
  }, []);

  // Blättern: beim Erscheinen, bei Hover und danach alle 20 s
  const [blaettern, setBlaettern] = useState(0);
  useEffect(() => {
    if (art !== 'buch' || bewegungAus()) return;
    const a = window.setTimeout(() => setBlaettern((x) => x + 1), 250);
    const takt = window.setInterval(() => { if (!document.hidden) setBlaettern((x) => x + 1); }, 20_000);
    return () => { window.clearTimeout(a); window.clearInterval(takt); };
  }, [art]);
  const hoverBlaettern = (e: React.PointerEvent) => { if (e.pointerType === 'mouse' && art === 'buch' && !bewegungAus()) setBlaettern((x) => x + 1); };

  const sichtbar = Boolean(art) && !aktiv && !verdeckt;
  const pauseStand = pause ? `${pause[1].schritt + 1}/${pauseAnleitung?.schritte.length ?? '?'}` : '';
  const titel = art === 'pause'
    ? t('Hilfe: geführter Zugriff unterbrochen bei Schritt {stand}', { stand: pauseStand })
    : t('Hilfe und Anleitungen');

  return (
    <>
      {sichtbar && (
        <button
          ref={knopfRef}
          type="button"
          className={`hi-symbol nicht-drucken${ueberHugo ? ' hi-ueber-hugo' : ''}${nebenHugo ? ' hi-neben-hugo' : ''}${art === 'pause' ? ' hi-symbol-pause' : ' hi-symbol-buch'}`}
          onClick={hilfeUmschalten}
          onPointerEnter={hoverBlaettern}
          aria-label={titel}
          title={titel}
          aria-expanded={panelOffen}
          aria-controls="hi-panel"
          data-hilfe-symbol={art ?? ''}
          data-tour="hilfe-symbol"
        >
          {art === 'pause' ? <Play size={18} className="hi-play" aria-hidden="true" /> : <BuchSymbol blaettern={blaettern} />}
          {art === 'pause' && <span className="hi-stand" aria-hidden="true">{pauseStand}</span>}
        </button>
      )}
      {panelOffen && !aktiv && <HilfePanel ueberHugo={ueberHugo} nebenHugo={Boolean(nebenHugo)} mitSymbol={sichtbar} knopf={knopfRef} />}
      {support && <SupportDialog vorlage={support} schliessen={() => setSupport(null)} />}
    </>
  );
}

function HilfePanel({ ueberHugo, nebenHugo, mitSymbol, knopf }: { ueberHugo: boolean; nebenHugo: boolean; mitSymbol: boolean; knopf: React.RefObject<HTMLButtonElement> }) {
  const { mandant } = useSession();
  const navigate = useNavigate();
  const { fortschritt } = useTour();
  const { liste, fehler } = useAnleitungen(mandant?.mandant_id);
  const [suche, setSuche] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const pause = pausierteTour(fortschritt);
  const pauseAnleitung = pause && liste ? liste.find((a) => a.id === pause[0]) ?? null : null;

  // Esc und Klick daneben schließen; Fokus beim Öffnen ins Suchfeld, beim Schließen zurück zum Symbol
  useEffect(() => {
    const vorher = document.activeElement as HTMLElement | null;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); hilfeSchliessen(); } };
    const daneben = (e: PointerEvent) => {
      const z = e.target as Node;
      if (panelRef.current?.contains(z) || knopf.current?.contains(z)) return;
      hilfeSchliessen();
    };
    window.addEventListener('keydown', esc, true);
    document.addEventListener('pointerdown', daneben, true);
    const fein = window.matchMedia('(pointer: fine)').matches;
    if (fein) panelRef.current?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
    else panelRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('keydown', esc, true);
      document.removeEventListener('pointerdown', daneben, true);
      const ziel = knopf.current ?? vorher;
      if (ziel && document.contains(ziel)) ziel.focus({ preventScroll: true });
    };
  }, [knopf]);

  const gruppen = useMemo(() => nachBereich((liste ?? []).filter((a) => anleitungPasst(a, suche))), [liste, suche]);

  const oeffnen = (a: Anleitung) => { hilfeSchliessen(); navigate(`/wiki/anleitungen/${a.id}`); };
  const zeigen = (a: Anleitung) => { void tourStarten(a.id); };

  return (
    <>
      <div className="hi-panel-schleier" aria-hidden="true" />
      <div
        ref={panelRef}
        id="hi-panel"
        className={`hi-panel${ueberHugo ? ' hi-ueber-hugo' : ''}${nebenHugo ? ' hi-neben-hugo' : ''}${mitSymbol ? ' hi-mit-symbol' : ''}`}
        role="dialog"
        aria-modal="false"
        aria-label={t('Hilfe und Anleitungen')}
        tabIndex={-1}
      >
        <div className="hi-panel-griff" aria-hidden="true" />
        <div className="hi-panel-kopf">
          <IcWiki className="hi-panel-icon" />
          <h2>{t('Hilfe und Anleitungen')}</h2>
          <Knopf art="leise" klein icon={<X />} onClick={hilfeSchliessen} aria-label={t('Schließen')} title={t('Schließen')} />
        </div>
        <div className="hi-panel-suche">
          <Suchfeld wert={suche} setzen={setSuche} platzhalter={t('Anleitungen durchsuchen')} />
        </div>
        <div className="hi-panel-inhalt">
          {pause && pauseAnleitung && (
            <div className="hi-weiter">
              <div className="hi-weiter-text">
                <strong>{t('Weitermachen: {titel}', { titel: pauseAnleitung.titel })}</strong>
                <span>{t('Schritt {n} von {m}', { n: pause[1].schritt + 1, m: pauseAnleitung.schritte.length })}</span>
              </div>
              <div className="hi-weiter-knoepfe">
                <Knopf art="leise" klein onClick={() => fortschrittVerwerfen(pause[0])}>{t('Verwerfen')}</Knopf>
                <Knopf art="primaer" klein icon={<Play />} onClick={() => { void tourFortsetzen(pause[0]); }} data-hilfe-fortsetzen="">{t('Fortsetzen')}</Knopf>
              </div>
              <div className="hi-weiter-balken" aria-hidden="true">
                <span style={{ transform: `scaleX(${(pause[1].schritt + 1) / Math.max(1, pauseAnleitung.schritte.length)})` }} />
              </div>
            </div>
          )}
          {liste == null ? (
            <div className="hi-leer">{t('Lädt …')}</div>
          ) : fehler && !liste.length ? (
            <div className="hi-leer">{fehler}</div>
          ) : gruppen.length === 0 ? (
            <div className="hi-leer">{suche ? t('Keine passende Anleitung. Versuche ein anderes Stichwort.') : t('Noch keine Anleitungen.')}</div>
          ) : gruppen.map((g) => (
            <section key={g.bereich} className="hi-gruppe" aria-label={g.bereich}>
              <h3 className="hi-gruppe-titel">{g.bereich}</h3>
              {g.anleitungen.map((a) => {
                const f = fortschritt[a.id];
                return (
                  <div key={a.id} className="hi-zeile">
                    <button type="button" className="hi-zeile-haupt" onClick={() => oeffnen(a)}>
                      <span className="hi-zeile-titel">
                        <span>{a.titel}</span>
                        {f?.status === 'beendet' && <Check size={14} className="hi-erledigt" aria-label={t('Erledigt')} />}
                      </span>
                      <span className="hi-zeile-kurz">{a.kurz}</span>
                      {!a.freigeschaltet && <span><Marke art="warn">{t('Für diesen Mandanten nicht freigeschaltet')}</Marke></span>}
                    </button>
                    {a.freigeschaltet && a.schritte.length > 0 && (
                      <button type="button" className="hi-zeigen" onClick={() => zeigen(a)} aria-label={t('Geführt zeigen: {titel}', { titel: a.titel })} title={t('Geführt zeigen')}>
                        <Play size={15} />
                      </button>
                    )}
                  </div>
                );
              })}
            </section>
          ))}
        </div>
        <div className="hi-panel-fuss">
          <span>{t('Keine passende Anleitung?')}</span>
          <Knopf klein icon={<LifeBuoy />} onClick={() => { hilfeSchliessen(); supportOeffnen({ quelle: 'hilfe', frage: suche.trim() || undefined }); }} data-tour="hilfe-support">{t('Frage an den Support')}</Knopf>
        </div>
      </div>
    </>
  );
}
